import { Unauthenticated, Authenticated } from '@/utils/auth/auth'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import { emailApplication } from '@/api/applicationsAPI'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import WestIcon from '@mui/icons-material/West'
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { AccessibleTooltip } from '@/components/AccessibleTooltip'

import { useNavigate, useParams } from 'react-router-dom'
import { useFocusManagement } from '@/utils/hooks/useFocusManagement'

import { UUID } from 'crypto'
import LoginSignup from '@/components/LoginSignup'
import { getEffectiveOrgUuid, getSafeExternalHref } from '@/utils/utils'
import { useApplication, useApplications } from '@/utils/hooks/applications'
import { useApplicationTask } from '@/utils/tasks/applicationTask'
import { useProfileRegenGuard } from '@/components/ProfileRegenBlockProvider'
import { getScanResultId } from '@/api/applicationsAPI'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApplicationTrackingData } from '@/models/Applications'
import QuestionAnswer from '@/components/QuestionAnswer'
import Loading from '@/components/Loading'

import { TZDate } from '@date-fns/tz'
import { format } from 'date-fns'
import ErrorMessage from '@/components/ErrorMessage'
import NotFound from './NotFound'
import { usePageTitle } from '@/utils/hooks/usePageTitle'
import { useOrgUsers } from '@/utils/hooks/invitations'
import { usePausedOrg } from '@/utils/hooks/usePausedOrg'
import UpgradeCTA from '@/components/UpgradeCTA'
import OutdatedQuestionsFeedbackButton from '@/components/OutdatedQuestionsFeedbackButton'
import { localisedDate, getOpportunityDisplayInfo } from '@/utils/dateUtils'

export default function ApplicationPage() {
  usePageTitle('Applications')
  const { canWrite } = usePausedOrg()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const focusRef = useFocusManagement()
  const guardRegen = useProfileRegenGuard()

  const [currentApplication, setCurrentApplication] = useState<
    ApplicationTrackingData | undefined
  >(undefined)
  const [emailing, setEmailing] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [emailSent, setEmailSent] = useState(false)
  const [updateError, setUpdateError] = useState<boolean>(false)

  const [regenerationError, setRegenerationError] = useState<string | null>(
    null,
  )
  const [regenerateConfirmDialog, setRegenerateConfirmDialog] = useState(false)
  const [scanId, setScanId] = useState<string | null>(null)

  const [generatingNewAnswers, setGeneratingNewAnswers] = useState(false)
  const [generateNewAnswersError, setGenerateNewAnswersError] = useState<
    string | null
  >(null)
  const [generateNewAnswersProgress, setGenerateNewAnswersProgress] = useState<{
    current: number
    total: number
  } | null>(null)
  const [generateNewAnswersSkipped, setGenerateNewAnswersSkipped] = useState<
    string | null
  >(null)
  const [skippedIndices, setSkippedIndices] = useState<Set<number>>(new Set())
  const [failedIndices, setFailedIndices] = useState<Set<number>>(new Set())

  const [updatingQuestions, setUpdatingQuestions] = useState(false)
  const [updateQuestionsError, setUpdateQuestionsError] = useState(false)
  const [hasUpdatedQuestions, setHasUpdatedQuestions] = useState(false)
  const [hasQuestionsToUpdate, setHasQuestionsToUpdate] = useState(false)

  // Upgrade state
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)
  const [upgradeReason, setUpgradeReason] = useState<string>('')

  // Editing state
  const [editingField, setEditingField] = useState<
    'comments' | 'owner_uuid' | null
  >(null)

  const [editValue, setEditValue] = useState<
    string | Date | null | number | undefined
  >(null)

  useEffect(() => {
    const hasQuestionsToAnswer =
      currentApplication?.answers?.answered_questions?.find(
        (answer) => answer.needs_answer === true,
      )

    if (hasQuestionsToAnswer) {
      setHasQuestionsToUpdate(true)
    } else {
      setHasQuestionsToUpdate(false)
    }
  }, [currentApplication?.answers?.answered_questions])

  const navigate = useNavigate()

  const handleEditClick = (
    field: 'comments' | 'owner_uuid',
    currentValue: string,
  ) => {
    setEditingField(field)
    setEditValue(currentValue)
  }

  const handleSaveEdit = () => {
    if (
      !editingField ||
      (editValue === null && editingField !== 'comments') ||
      !currentApplication
    ) {
      return
    }

    setUpdateError(false)

    let processedValue: string | Date | null | number | undefined = editValue

    if (
      (editingField === 'owner_uuid' && editValue === 'None') ||
      editValue === ''
    ) {
      processedValue = null
    }

    updateApplicationTracking.mutate(
      {
        token: token as string,
        orgUuid: effectiveOrgUuid,
        applicationId: currentApplication.id as number,
        data: {
          [editingField as string]: processedValue,
        },
      },
      {
        onError: () => {
          setUpdateError(true)
        },
      },
    )

    setEditingField(null)
    setEditValue(null)
  }

  const handleCancelEdit = () => {
    setEditingField(null)
    setEditValue(null)
  }

  async function handleGenerateNewAnswers() {
    if (!token || !currentApplication) return
    const newQuestions = (currentApplication.answers?.answered_questions ?? [])
      .map((q, i) => ({ q, i }))
      .filter(({ q }) => q.needs_answer === true)

    setGeneratingNewAnswers(true)
    setGenerateNewAnswersError(null)
    setGenerateNewAnswersSkipped(null)
    setSkippedIndices(new Set())
    setFailedIndices(new Set())
    setGenerateNewAnswersProgress({ current: 0, total: newQuestions.length })
    let failed = 0
    let skipped = 0
    const skippedSet = new Set<number>()
    const failedSet = new Set<number>()
    for (let n = 0; n < newQuestions.length; n++) {
      const { i } = newQuestions[n]
      setGenerateNewAnswersProgress({
        current: n + 1,
        total: newQuestions.length,
      })
      try {
        const result = await regenerateAnswer.mutateAsync({
          token,
          applicationId: Number(applicationId),
          answerIndex: i,
          answerFeedback: 'New question',
          regeneratedAnswer: null,
        })
        if (result.newAnswer === 'INSUFFICIENT_INFORMATION') {
          skipped++
          skippedSet.add(i)
          continue
        }
        await saveAnswer.mutateAsync({
          token,
          applicationId: Number(applicationId),
          answerIndex: i,
          newAnswer: result,
        })
      } catch (error) {
        // Profile regenerating → stop the batch and show the shared dialog
        // rather than marking every remaining question as failed.
        if (guardRegen(error)) {
          setGeneratingNewAnswers(false)
          setGenerateNewAnswersProgress(null)
          return
        }
        failed++
        failedSet.add(i)
      }
    }
    setSkippedIndices(skippedSet)
    setFailedIndices(failedSet)
    setGeneratingNewAnswers(false)
    setGenerateNewAnswersProgress(null)
    if (skipped > 0) {
      setGenerateNewAnswersSkipped(
        `Couldn't generate ${skipped === 1 ? 'an answer' : 'answers'} for ${skipped} question${skipped > 1 ? 's' : ''} — we didn't have enough information. Please use the Edit button to add ${skipped > 1 ? 'those answers' : 'that answer'} manually.`,
      )
    }
    if (failed > 0) {
      setGenerateNewAnswersError(
        `${failed} question${failed > 1 ? 's' : ''} could not be generated — try using the Generate Answer button next to ${failed > 1 ? 'them' : 'it'}.`,
      )
    }
  }

  async function handleRegenerateApplication() {
    if (!token || !applicationId || !scanId || !currentApplication) return
    setRegenerationError(null)
    setRegenerateConfirmDialog(false)
    await startApplicationRegeneration(
      Number(applicationId),
      scanId,
      Number(currentApplication.need_id),
      String(currentApplication.org_uuid),
    )
  }

  function handleGoBack() {
    const canGoBack = window.history.state && window.history.state.idx > 0

    if (canGoBack) {
      // is navigating from same origin
      navigate(-1)
    } else {
      // go to applications for that need
      navigate(`/applications/${selectedNeed}`)
    }
  }

  const params = useParams()
  const applicationId = params.applicationId
  const selectedNeed = params.fundingNeedId

  const { user, token, isLoading: userLoading, orgs } = getCurrentUser()
  const queryClient = useQueryClient()

  if (!userLoading && orgs && orgs.length === 0) {
    window.location.href = '/'
  }

  const {
    orgData,
    orgErrors,
    isLoading: orgsLoading,
    isError,
  } = useCurrentUserOrgs(token as string, user, orgs as UUID[])

  const effectiveOrgUuid = getEffectiveOrgUuid(user, orgs)

  const {
    updateApplicationTracking,
    updateApplicationQuestions,
    regenerateAnswer,
    saveAnswer,
  } = useApplications(
    token as string,
    effectiveOrgUuid,
    Number(currentApplication?.need_id),
    currentApplication?.id,
  )

  const organisation = orgData.find((org) => org.uuid === effectiveOrgUuid)

  const { data: orgUsers, isLoading: orgUsersLoading } = useOrgUsers(
    token as string,
    effectiveOrgUuid,
  )
  const {
    data: applicationData,
    isLoading: applicationsLoading,
    error: applicationsError,
  } = useApplication(token as string, Number(applicationId), effectiveOrgUuid)

  const {
    startApplicationRegeneration,
    hasActiveApplicationGeneration,
    isLoadingForScanId,
    progressForScanId,
  } = useApplicationTask({
    token: token as string,
    onCompleted: () => {
      queryClient.invalidateQueries({
        queryKey: ['application', Number(applicationId)],
      })
    },
    onError: (error) => {
      if (guardRegen(error)) return
      setRegenerationError(error.message || 'Failed to regenerate application')
    },
  })

  const regenerating = scanId
    ? hasActiveApplicationGeneration(scanId) || isLoadingForScanId(scanId)
    : false
  const regeneratingProgress = scanId ? progressForScanId(scanId) : 0

  useEffect(() => {
    if (!token) {
      return
    }
    if (applicationData) {
      setCurrentApplication(applicationData)
    }
  }, [applicationData, token])

  // Look up scan_result.id for this application so regen-tracking can key by it.
  useEffect(() => {
    if (
      !token ||
      !currentApplication?.org_uuid ||
      !currentApplication?.need_id ||
      !currentApplication?.opportunity_uuid
    ) {
      return
    }
    getScanResultId(
      token,
      String(currentApplication.org_uuid),
      Number(currentApplication.need_id),
      String(currentApplication.opportunity_uuid),
    ).then((id) => setScanId(id ? String(id) : null))
  }, [
    token,
    currentApplication?.org_uuid,
    currentApplication?.need_id,
    currentApplication?.opportunity_uuid,
  ])

  if (userLoading || orgsLoading || applicationsLoading || orgUsersLoading) {
    return <Loading />
  }

  if (
    (currentApplication &&
      currentApplication.need_id !== Number(selectedNeed)) ||
    (currentApplication && currentApplication.id !== Number(applicationId)) ||
    isNaN(Number(selectedNeed)) ||
    isNaN(Number(applicationId)) ||
    !currentApplication
  ) {
    return <NotFound />
  }

  if (isError || applicationsError || orgErrors.length > 0) {
    return <ErrorMessage />
  }

  async function handleEmailApplication() {
    if (!currentApplication || !token) {
      return
    }

    setEmailError(null)
    setEmailSent(false)
    setEmailing(true)

    try {
      const response = await emailApplication(token, currentApplication.id)

      if (response.error !== null) {
        setEmailError('Error sending email')
      }

      if (response.success) {
        setEmailError(null)
        setEmailSent(true)
      }
    } catch {
      setEmailError('Error sending email')
    } finally {
      setEmailing(false)
    }
  }

  async function handleUpdateQuestions() {
    if (!currentApplication || !token) {
      return
    }

    setUpdatingQuestions(true)
    setUpdateQuestionsError(false)
    setHasUpdatedQuestions(false)
    setGenerateNewAnswersSkipped(null)
    setGenerateNewAnswersError(null)
    setSkippedIndices(new Set())
    setFailedIndices(new Set())

    updateApplicationQuestions.mutate(
      {
        token: token as string,
        orgUuid: effectiveOrgUuid,
        applicationId: currentApplication.id as number,
      },
      {
        onSuccess: () => {
          setHasUpdatedQuestions(true)
        },
        onError: () => {
          setUpdateQuestionsError(true)
        },
        onSettled: () => {
          setUpdatingQuestions(false)
        },
      },
    )
  }

  const applicationDisplayInfo = currentApplication
    ? getOpportunityDisplayInfo(
        { always_open: currentApplication.always_open ?? false },
        currentApplication.application_open_close_dates ?? [],
      )
    : null

  return (
    <>
      <Unauthenticated>
        <LoginSignup />
      </Unauthenticated>
      <Authenticated>
        <>
          {!user || !token || (!organisation && <ErrorMessage />)}

          {/* Upgrade CTA Dialog */}
          <UpgradeCTA
            variant="dialog"
            message={upgradeReason || 'Upgrade required to access this feature'}
            restrictedFeature="Email application"
            isOpen={showUpgradeDialog}
            onClose={() => setShowUpgradeDialog(false)}
          />

          {user && token && organisation && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'left',
                flexDirection: 'column',
                width: '90vw',
                margin: '0 auto 20px',
              }}
            >
              {applicationData && applicationId && currentApplication ? (
                <>
                  <Button
                    disableRipple
                    sx={{
                      mb: 5,
                      textTransform: 'none',
                      fontSize: '18px',
                      color: '#1C3045',
                      width: isMobile ? '55vw' : '150px',
                      justifyContent: 'flex-start',
                      mt: 1.5,
                      fontWeight: 400,
                      '&:focus': {
                        backgroundColor: '#e8e8e8',
                      },
                    }}
                    onClick={handleGoBack}
                  >
                    <WestIcon
                      sx={{
                        mr: isMobile ? '5%' : '20%',
                        fontSize: '18px',
                      }}
                    />
                    Go Back
                  </Button>
                  <Typography
                    variant="h1"
                    component="h1"
                    ref={focusRef}
                    tabIndex={-1}
                    sx={{
                      mb: 1,
                      textAlign: 'left',
                      fontSize: '34px',
                      '&:focus': {
                        outline: 'none',
                      },
                    }}
                  >
                    {currentApplication.funding_need_title}{' '}
                    {currentApplication.funding_need_is_archived
                      ? '(Archived)'
                      : ''}{' '}
                    Application
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Box>
                      <Typography component="h2" variant="h5" sx={{ my: 1 }}>
                        {currentApplication.fund_name
                          ? `Application for ${currentApplication.fund_name} -
                          ${currentApplication.funder_name}`
                          : `Application for
                          ${currentApplication.funder_name}`}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: isMobile ? 1 : 3 }}>
                        Application Last updated:{' '}
                        {localisedDate(new Date(currentApplication.updated_at))}
                      </Typography>
                      {applicationDisplayInfo?.message && (
                        <Box
                          sx={{
                            mb: isMobile ? 1 : 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            width: 'fit-content',
                            maxWidth: '100%',
                          }}
                        >
                          {applicationDisplayInfo.status === 'closed' ? (
                            <EventBusyOutlinedIcon
                              fontSize="small"
                              sx={{ color: 'text.secondary', flexShrink: 0 }}
                            />
                          ) : applicationDisplayInfo.status ===
                            'closing-soon' ? (
                            <AccessTimeOutlinedIcon
                              fontSize="small"
                              sx={{ color: 'text.secondary', flexShrink: 0 }}
                            />
                          ) : (
                            <EventAvailableOutlinedIcon
                              fontSize="small"
                              sx={{ color: 'text.secondary', flexShrink: 0 }}
                            />
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {applicationDisplayInfo.message}
                          </Typography>
                          {applicationDisplayInfo.isEstimate && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                flexShrink: 0,
                              }}
                            >
                              <AccessibleTooltip title="These round dates are estimated. They will be confirmed when more information is available.">
                                <span
                                  role="img"
                                  aria-label="Info: These round dates are estimated. They will be confirmed when more information is available."
                                  tabIndex={0}
                                >
                                  <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                                </span>
                              </AccessibleTooltip>
                              <Typography variant="body2" fontStyle="italic">
                                Estimate Only
                              </Typography>
                            </Box>
                          )}
                          {applicationDisplayInfo.status === 'closing-soon' && (
                            <Chip
                              label="Closing Soon"
                              size="small"
                              variant="outlined"
                              color="warning"
                              sx={{
                                fontSize: '11px',

                                bgcolor: 'white',
                                fontWeight: 600,
                                flexShrink: 0,
                                '& .MuiChip-label': {
                                  lineHeight: 1,
                                  px: 1,
                                },
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',

                        gap: 2,
                        mt: isMobile ? 2 : 1,
                        mb: isMobile ? 2 : 1,
                      }}
                    >
                      {emailError ? (
                        <Alert severity="error" sx={{ width: '250px' }}>
                          {emailError}
                        </Alert>
                      ) : (
                        <Button
                          variant="contained"
                          disableRipple
                          sx={{
                            width: '250px',
                            textTransform: 'none',
                            fontSize: '18px',
                            backgroundColor: '#1C3045',
                          }}
                          disabled={
                            !currentApplication ||
                            emailing ||
                            emailSent ||
                            !token ||
                            regenerating
                          }
                          onClick={() => handleEmailApplication()}
                        >
                          {emailing ? (
                            <Typography
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: '#1C3045',
                                fontWeight: '500',
                                fontSize: '18px',
                                py: 0.5,
                              }}
                            >
                              <CircularProgress
                                size={18}
                                sx={{ color: '#1C3045', mr: 2 }}
                              />
                              Sending...
                            </Typography>
                          ) : emailSent ? (
                            <Typography
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: '#1C3045',
                                fontWeight: '500',
                                fontSize: '18px',
                                py: 0.5,
                              }}
                            >
                              Email sent
                              <CheckCircleOutlineOutlinedIcon
                                sx={{
                                  color: '#1C3045',
                                  ml: 2,
                                  fontSize: 24,
                                }}
                              />
                            </Typography>
                          ) : (
                            <Typography
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                fontWeight: '500',
                                fontSize: '18px',
                                py: 0.5,
                              }}
                            >
                              Email me a copy
                            </Typography>
                          )}
                        </Button>
                      )}

                      <Button
                        variant="outlined"
                        disableRipple
                        disabled={regenerating || !canWrite || !scanId}
                        onClick={() => setRegenerateConfirmDialog(true)}
                        sx={{
                          width: '250px',
                          textTransform: 'none',
                          fontSize: '18px',
                          color: '#1C3045',
                          borderColor: '#1C3045',
                        }}
                      >
                        Regenerate Application
                      </Button>
                      {regenerating && (
                        <Box sx={{ width: '250px' }}>
                          <LinearProgress
                            variant="determinate"
                            value={regeneratingProgress || 0}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: '#E0E0E0',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: '#1C3045',
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: 0.75,
                              mt: 0.5,
                              color: '#666',
                            }}
                          >
                            Regenerating... {regeneratingProgress || 0}%
                            <CircularProgress
                              size={12}
                              sx={{ color: '#666' }}
                            />
                          </Typography>
                        </Box>
                      )}
                      {regenerationError && (
                        <Alert severity="error" sx={{ maxWidth: '250px' }}>
                          {regenerationError}
                        </Alert>
                      )}
                    </Box>
                  </Box>

                  {/* Draft Application Disclaimer */}
                  {(!currentApplication.status ||
                    currentApplication.status === 'draft') && (
                    <Alert
                      severity="warning"
                      sx={{
                        mb: 2,
                        borderRadius: 1,
                        width: '100%',
                        maxWidth: '1400px',
                        pr: 10,
                      }}
                    >
                      <AlertTitle>
                        Top tips for drafting applications
                      </AlertTitle>
                      <Box
                        component="ul"
                        sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}
                      >
                        <li>
                          Fundsorter doesn't submit applications to funders. It
                          will help you create a draft, then you apply to the
                          funder yourself.
                        </li>
                        <li>
                          The system learns from you - if you make corrections
                          to answers in one draft, the system will remember what
                          it learns to improve your future drafts.
                        </li>
                        <li>
                          Click the 'Regenerate Answer' icon for AI assisted
                          re-drafting of questions - tell it what you want to
                          change and the system will improve the text.
                        </li>
                        <li>
                          Click the Pencil Icon to edit a question manually.
                        </li>
                        <li>
                          When you're done editing and happy with the answers,
                          click "Email me a copy" to get a finalised version of
                          the application, ready to submit to the funder.
                        </li>
                      </Box>
                    </Alert>
                  )}

                  {/* Editable Application Details */}
                  {currentApplication && (
                    <Box
                      sx={{
                        mb: 1,
                        ml: 2,
                        width: '100%',
                        // Pulls this block up to compensate for the "Draft Application
                        // Disclaimer" alert above being hidden once status leaves
                        // 'draft' — unrelated to the status tag/editing that used to
                        // render directly below this Box.
                        mt:
                          currentApplication.status &&
                          currentApplication.status != 'draft' &&
                          !isMobile
                            ? -3
                            : 0,
                      }}
                    >
                      {/* Status tag and editing removed from drafting page — temporary
                          stopgap per #1872 until round-differentiation design is finalized. */}

                      {/* Owner Section */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2">
                          Owner:{' '}
                          {editingField === 'owner_uuid' ? (
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                ml: 1,
                              }}
                            >
                              <Select
                                size="small"
                                value={editValue ?? 'None'}
                                onChange={(e) => setEditValue(e.target.value)}
                                sx={{ minWidth: '120px' }}
                                autoFocus
                              >
                                <MenuItem value="None">No owner</MenuItem>
                                {orgUsers &&
                                  orgUsers.length > 0 &&
                                  orgUsers
                                    .filter(
                                      (user) =>
                                        user.first_name && user.last_name,
                                    )
                                    .map((users) => (
                                      <MenuItem
                                        key={users.uuid}
                                        value={users.uuid}
                                      >
                                        {users.first_name} {users.last_name}
                                      </MenuItem>
                                    ))}
                              </Select>

                              <Button
                                disableRipple
                                size="small"
                                onClick={handleSaveEdit}
                                sx={{ textTransform: 'none' }}
                              >
                                Save
                              </Button>

                              <Button
                                disableRipple
                                size="small"
                                onClick={handleCancelEdit}
                                sx={{ textTransform: 'none' }}
                              >
                                Cancel
                              </Button>
                            </Box>
                          ) : (
                            <span
                              style={{
                                fontStyle: currentApplication.owner_name
                                  ? 'normal'
                                  : 'italic',
                                color: currentApplication.owner_name
                                  ? 'inherit'
                                  : '#666666',
                              }}
                            >
                              {currentApplication.owner_name ||
                                'No owner assigned'}
                            </span>
                          )}
                        </Typography>
                        {editingField !== 'owner_uuid' && (
                          <IconButton
                            disableRipple
                            size="small"
                            aria-label="edit owner"
                            disabled={!canWrite}
                            onClick={() =>
                              handleEditClick(
                                'owner_uuid',
                                currentApplication.owner_uuid || '',
                              )
                            }
                            sx={{ minWidth: 'auto', p: 0.25 }}
                          >
                            <EditOutlinedIcon
                              sx={{
                                fontSize: '16px',
                                color: '#1C3045',
                                '&:focus': {
                                  bgcolor: 'grey.300',
                                },
                                '&:hover': {
                                  bgcolor: 'grey.200',
                                },
                              }}
                            />
                          </IconButton>
                        )}
                      </Box>

                      {/* Notes Section */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems:
                            editingField === 'comments'
                              ? 'flex-start'
                              : 'center',
                          gap: 0.5,
                          width: '100%',
                        }}
                      >
                        {editingField === 'comments' ? (
                          <Box sx={{ width: '100%' }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              Notes:
                            </Typography>
                            <TextField
                              size="small"
                              multiline
                              rows={3}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              placeholder="Add notes..."
                              sx={{
                                width: '50vw',
                                maxWidth: 'none',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#757575',
                                },
                              }}
                              autoFocus
                            />
                            <Box sx={{ mt: 1 }}>
                              <Button
                                disableRipple
                                size="small"
                                onClick={handleSaveEdit}
                                sx={{ textTransform: 'none', mr: 1 }}
                              >
                                Save
                              </Button>
                              <Button
                                disableRipple
                                size="small"
                                onClick={handleCancelEdit}
                                sx={{ textTransform: 'none' }}
                              >
                                Cancel
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            Notes:{' '}
                            <span
                              style={{
                                fontStyle: currentApplication.comments
                                  ? 'normal'
                                  : 'italic',
                                color: currentApplication.comments
                                  ? 'inherit'
                                  : '#666666',
                              }}
                            >
                              {currentApplication.comments || 'No notes added'}
                            </span>
                          </Typography>
                        )}
                        {editingField !== 'comments' && (
                          <IconButton
                            disableRipple
                            aria-label="edit notes"
                            size="small"
                            disabled={!canWrite}
                            onClick={() =>
                              handleEditClick(
                                'comments',
                                currentApplication.comments || '',
                              )
                            }
                            sx={{ minWidth: 'auto', p: 0.25 }}
                          >
                            <EditOutlinedIcon
                              sx={{
                                fontSize: '16px',
                                color: '#1C3045',
                                '&:focus': {
                                  bgcolor: 'grey.300',
                                },
                                '&:hover': {
                                  bgcolor: 'grey.200',
                                },
                              }}
                            />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  )}

                  {updateError && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 2,
                        borderRadius: 1,
                      }}
                    >
                      Sorry, something went wrong updating the application.
                      Please try again later.
                    </Alert>
                  )}

                  {currentApplication.questions_outdated ? (
                    <>
                      <Alert
                        severity="warning"
                        sx={{
                          mb: 2,
                          borderRadius: 1,
                          width: '100%',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ flexGrow: 1, width: '100vw' }}
                          >
                            The questions for this application are outdated.
                            Please click the 'Update Questions' button to update
                            them.
                          </Typography>
                          <Button
                            disableRipple
                            disabled={updatingQuestions}
                            variant="contained"
                            onClick={handleUpdateQuestions}
                            sx={{
                              textTransform: 'none',
                              color: 'white',
                              backgroundColor: '#1C3045',
                              borderColor: '#1C3045',
                              borderWidth: 2,
                              borderRadius: 1,
                              width: '250px',
                            }}
                          >
                            {updatingQuestions ? (
                              <Typography>
                                <CircularProgress size={20} /> Updating...{' '}
                              </Typography>
                            ) : (
                              'Update Questions'
                            )}
                          </Button>
                        </Box>
                      </Alert>
                    </>
                  ) : hasUpdatedQuestions ? (
                    <>
                      <Alert
                        severity="success"
                        sx={{
                          mb: 2,
                          borderRadius: 1,
                        }}
                      >
                        Questions updated successfully! <br />
                        If a question is new or has changed, you will need to
                        reanswer it. Feel free add your own answer with the
                        'Edit Answer' button, or use the 'Generate Answer'
                        button next to the question to generate an answer.
                        <br />
                        <span style={{ fontWeight: '500' }}>
                          {' '}
                          Remember to email yourself the updated copy of the
                          application when you have finished.
                        </span>
                        {currentApplication.answers?.answered_questions?.some(
                          (q) => q.needs_answer,
                        ) && (
                          <Box sx={{ mt: 1.5 }}>
                            <Button
                              variant="contained"
                              disabled={generatingNewAnswers}
                              onClick={handleGenerateNewAnswers}
                              sx={{
                                textTransform: 'none',
                                backgroundColor: '#1C3045',
                                '&:hover': { backgroundColor: '#2a4562' },
                              }}
                            >
                              {generatingNewAnswers &&
                              generateNewAnswersProgress ? (
                                <>
                                  <CircularProgress
                                    size={18}
                                    sx={{ color: 'white', mr: 1 }}
                                  />
                                  Generating{' '}
                                  {generateNewAnswersProgress.current} of{' '}
                                  {generateNewAnswersProgress.total}...
                                </>
                              ) : (
                                'Generate answers to new questions'
                              )}
                            </Button>
                          </Box>
                        )}
                      </Alert>
                      {generateNewAnswersSkipped && (
                        <Alert
                          severity="warning"
                          sx={{ mb: 2, borderRadius: 1 }}
                        >
                          {generateNewAnswersSkipped}
                        </Alert>
                      )}
                      {generateNewAnswersError && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
                          {generateNewAnswersError}
                        </Alert>
                      )}
                    </>
                  ) : hasQuestionsToUpdate ? (
                    <>
                      <Alert
                        severity="info"
                        sx={{
                          mb: 2,
                          borderRadius: 1,
                        }}
                      >
                        The questions for this application have been updated.
                        <br />
                        Some of the questions are new or have changed and need
                        to be answered. Feel free add your own answer with the
                        'Edit Answer' button, or use the 'Generate Answer'
                        button next to the question to generate an answer.
                        <br />
                        <span style={{ fontWeight: '500' }}>
                          {' '}
                          Remember to email yourself the updated copy of the
                          application when you have finished.
                        </span>
                        {currentApplication.answers?.answered_questions?.some(
                          (q) => q.needs_answer,
                        ) && (
                          <Box sx={{ mt: 1.5 }}>
                            <Button
                              variant="contained"
                              disabled={generatingNewAnswers}
                              onClick={handleGenerateNewAnswers}
                              sx={{
                                textTransform: 'none',
                                backgroundColor: '#1C3045',
                                '&:hover': { backgroundColor: '#2a4562' },
                              }}
                            >
                              {generatingNewAnswers &&
                              generateNewAnswersProgress ? (
                                <>
                                  <CircularProgress
                                    size={18}
                                    sx={{ color: 'white', mr: 1 }}
                                  />
                                  Generating{' '}
                                  {generateNewAnswersProgress.current} of{' '}
                                  {generateNewAnswersProgress.total}...
                                </>
                              ) : (
                                'Generate answers to new questions'
                              )}
                            </Button>
                          </Box>
                        )}
                      </Alert>
                      {generateNewAnswersSkipped && (
                        <Alert
                          severity="warning"
                          sx={{ mb: 2, borderRadius: 1 }}
                        >
                          {generateNewAnswersSkipped}
                        </Alert>
                      )}
                      {generateNewAnswersError && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
                          {generateNewAnswersError}
                        </Alert>
                      )}
                    </>
                  ) : null}
                  {updateQuestionsError && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 2,
                        borderRadius: 1,
                      }}
                    >
                      Sorry, something went wrong updating the questions. Please
                      try again later.
                    </Alert>
                  )}

                  {/* Outdated Questions Feedback */}
                  {currentApplication &&
                    token &&
                    currentApplication.opportunity_uuid && (
                      <Box
                        sx={{
                          mt: isMobile ? 4 : 0,
                          mb: isMobile ? 1.5 : 2,
                          display: 'flex',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <OutdatedQuestionsFeedbackButton
                          token={token}
                          orgUuid={effectiveOrgUuid}
                          needId={currentApplication.need_id}
                          opportunityUuid={currentApplication.opportunity_uuid}
                        />
                      </Box>
                    )}

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      border: 1,
                      width: '90vw',
                      borderColor: '#1C3045',
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      filter: regenerating ? 'blur(2px)' : 'none',
                      pointerEvents: regenerating ? 'none' : 'auto',
                      transition: !regenerating ? 'filter 0.5s ease' : 'none',
                    }}
                  >
                    {currentApplication.answers?.answered_questions?.map(
                      (data, index, questions) => {
                        const currentSection = data.question_metadata?.section
                        const previousSection =
                          index > 0
                            ? questions[index - 1]?.question_metadata?.section
                            : null
                        const nextSection =
                          index < questions.length - 1
                            ? questions[index + 1]?.question_metadata?.section
                            : null

                        const isFirstInSection =
                          currentSection && currentSection !== previousSection
                        const isLastInSection =
                          currentSection && currentSection !== nextSection

                        return (
                          <Box key={index}>
                            {/* Top divider if this is the first question in a section */}
                            {isFirstInSection && (
                              <>
                                <Typography
                                  variant="h6"
                                  sx={{ mt: 3, mb: 0.5, ml: 1 }}
                                >
                                  {currentSection}
                                </Typography>
                                <Divider
                                  sx={{
                                    mb: 2,
                                    borderWidth: 1,
                                    borderColor: '#1C3045',
                                  }}
                                />
                              </>
                            )}

                            {/* The question */}
                            <QuestionAnswer
                              data={data}
                              index={index}
                              token={token}
                              applicationId={Number(applicationId)}
                              orgId={effectiveOrgUuid}
                              setEmailSent={setEmailSent}
                              readOnly={!canWrite}
                              isLastInSection={isLastInSection || false}
                              regenState={
                                skippedIndices.has(index)
                                  ? 'skipped'
                                  : failedIndices.has(index)
                                    ? 'failed'
                                    : undefined
                              }
                            />

                            {/* Bottom divider if this is the last question in a section */}
                            {isLastInSection && (
                              <Divider
                                sx={{
                                  my: 2,
                                  borderWidth: 1,
                                  borderColor: '#1C3045',
                                }}
                              />
                            )}
                          </Box>
                        )
                      },
                    )}
                  </Box>
                </>
              ) : null}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'left',
                  flexDirection: 'column',
                  width: '90vw',
                  margin: '0 auto',
                  bgcolor: '#E9E9E9',
                  mt: 1,
                  mb: 2,
                  borderRadius: 2,
                  py: 1,
                }}
              >
                <Typography variant="h5" sx={{ my: 2, mx: 2 }}>
                  Application Information
                </Typography>

                {currentApplication.application_info &&
                  currentApplication.application_info.length > 0 && (
                    <Box component="ul" sx={{ mx: 2, my: 1, pl: 3 }}>
                      {currentApplication.application_info
                        .replace(/\\n/g, '\n')
                        .split('\n')
                        .filter((item) => item.trim().length > 0)
                        .map((item, index) => (
                          <li key={index}>
                            <Typography variant="body1">{item}</Typography>
                          </li>
                        ))}
                    </Box>
                  )}
                {/* Application link - shown independently of application_info,
                and unconditionally (with a fallback) */}
                <Typography variant="body1" sx={{ mx: 2, mb: 1 }}>
                  {(() => {
                    const safeHref = currentApplication.application_link
                      ? getSafeExternalHref(currentApplication.application_link)
                      : null
                    return safeHref ? (
                      <>
                        <span style={{ fontWeight: 500 }}>Application link:</span>{' '}
                        <Link href={safeHref} target="_blank" rel="noopener noreferrer">
                          {currentApplication.application_link}
                        </Link>
                      </>
                    ) : (
                      <i>
                        Sorry, we don&apos;t have an application link on file -
                        Please contact the funder directly
                      </i>
                    )
                  })()}
                </Typography>
              </Box>
            </Box>
          )}
        </>
      </Authenticated>

      {/* Regenerate Application Confirmation Dialog */}
      <Dialog
        open={regenerateConfirmDialog}
        onClose={() => setRegenerateConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '24px', fontWeight: 500 }}>
          Regenerate Application
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            This is useful if you have uploaded documents or websites since the
            application was created and want to update the whole application
            using the new content.
          </Typography>
          <Alert severity="warning" sx={{ my: 3 }}>
            This will regenerate all answers in the application. <br />
            Any edits you have made to the answers will be overwritten.
          </Alert>
          <Typography variant="body1">
            Are you sure you want to continue?
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic' }}
          >
            Tip: If you want a backup copy of the current application and
            answers, email a copy to yourself first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            disableRipple
            onClick={() => setRegenerateConfirmDialog(false)}
            sx={{ textTransform: 'none' }}
          >
            Go Back
          </Button>
          <Button
            disableRipple
            onClick={handleRegenerateApplication}
            variant="contained"
            sx={{
              textTransform: 'none',
              backgroundColor: '#1C3045',
              '&:hover': { backgroundColor: '#2a4562' },
            }}
          >
            Regenerate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
